<template>
  <Slice>
    <div class="slice-embed" ref="embed" :style="containerStyles">
      <!-- Try oEmbed first for supported providers -->
      <prismic-embed
          v-if="shouldUseOEmbed"
          ref="obj"
          :field="embedField"
          @load="onOEmbedLoad"
          @error="onOEmbedError"
      />

      <!-- Fallback to iframe for any website -->
      <div v-else class="iframe-container" :style="iframeContainerStyles">
        <iframe
            ref="iframe"
            :src="embedUrl"
            :title="embedTitle"
            frameborder="0"
            allowfullscreen
            allow="geolocation"
            :style="iframeStyles"
            @load="onIframeLoad"
            @error="onIframeError"
        ></iframe>


        <!-- Loading indicator -->
        <div v-if="isLoading" class="loading-indicator">
          <div class="spinner"></div>
          <span>Loading...</span>
        </div>

        <!-- Error fallback -->
        <div v-if="hasError" class="error-fallback">
          <div class="error-content">
            <h3>Unable to embed this content</h3>
            <p>This website cannot be embedded directly.</p>
            <a :href="embedUrl" target="_blank" rel="noopener noreferrer" class="external-link">
              Visit {{ embedTitle || 'this website' }} →
            </a>
          </div>
        </div>
      </div>
    </div>
  </Slice>
</template>

<script>
export default {
  name: "sliceiframeembed",
  props: ['item', 'slice', 'url'],
  components: {},
  data() {
    return {
      width: 0,
      height: 0,
      type: null,
      isLoading: false,
      hasError: false,
      oEmbedFailed: false,
      defaultHeight: 300
    }
  },
  mounted() {
    this.getWidth()
    this.initEmbed()
  },
  created() {
    window.addEventListener("resize", this.getWidth)
  },
  destroyed() {
    window.removeEventListener("resize", this.getWidth)
  },
  computed: {

    isFA() {
      return this.isFormAssembly();
    },
    embedUrl() {
      const field = this.url || this.slice?.primary?.url
      return typeof field === 'string' ? field : field?.url || field?.embed_url
    },

    embedHeight(){
      return this.heightOverride
    },

    embedField() {
      return this.url || this.slice?.primary?.url
    },

    embedTitle() {
      const field = this.embedField
      return field?.title || field?.provider_name || 'Embedded Content'
    },

    shouldUseOEmbed() {
      // Check if we have a proper oEmbed field with HTML content
      const field = this.embedField
      return !this.oEmbedFailed &&
          field &&
          typeof field === 'object' &&
          field.html &&
          this.isOEmbedProvider(this.embedUrl)
    },

    containerStyles() {
      if (this.type === 'video' || this.isVideoUrl(this.embedUrl)) {
        return {
          height: this.adjustedHeightForVideo + 'px',
          'aspect-ratio': '16/9'
        }
      }
      console.log(this.slice?.primary?.heightOverride)
      if(this.slice?.primary?.heightOverride != null){
        return {
          height: this.slice?.primary?.heightOverride + 'px'
        }
      } else return {
        height: this.defaultHeight + 'px'
      }
    },

    iframeContainerStyles() {
      return {
        position: 'relative',
        width: '100%',
        height: '100%',
      }
    },

    iframeStyles() {
      return {
        width: '100%',
        height: '100%',
        border: 'none',
        display: this.isLoading || this.hasError ? 'none' : 'block'
      }
    },

    adjustedHeightForVideo() {
      return this.width > 0 ? Math.round(this.width * 0.5625) : 315 // 16:9 ratio
    }
  },
  methods: {
    initEmbed() {
      if (!this.shouldUseOEmbed) {
        this.isLoading = true
        this.hasError = false
      }
    },

    getWidth() {
      if (this.$refs.embed) {
        this.width = this.$refs.embed.clientWidth
      }

      // Get oEmbed type if available
      if (this.$refs.obj?.dataset?.oembedType) {
        this.type = this.$refs.obj.dataset.oembedType
      }
    },

    isOEmbedProvider(url) {
      if (!url) return false

      const oEmbedProviders = [
        'youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com',
        'twitter.com', 'x.com', 'instagram.com', 'facebook.com',
        'tiktok.com', 'spotify.com', 'soundcloud.com',
        'codepen.io', 'jsfiddle.net', 'codesandbox.io'
      ]

      return oEmbedProviders.some(provider => url.includes(provider))
    },

    isVideoUrl(url) {
      if (!url) return false
      return this.isOEmbedProvider(url) || url.includes('video')
    },

    isFormAssembly(url) {
      if(url.includes("tfaforms.com")) {
        /*
 * Helper script for iframe-published FormAssembly.com forms.
 * Auto-resize iframe to fit content.
 */
        (function () {

          /*
           * A backwards compatible implementation of postMessage
           * by Josh Fraser (joshfraser.com)
           * released aunder the Apache 2.0 license.
           *
           * This code was adapted from Ben Alman's jQuery postMessage code found at:
           * http://benalman.com/projects/jquery-postmessage-plugin/
           *
           * Other inspiration was taken from Luke Shepard's code for Facebook Connect:
           * http://github.com/facebook/connect-js/blob/master/src/core/xd.js
           */
          var XD = function () {

            var interval_id,
                last_hash,
                cache_bust = 1,
                attached_callback,
                window = this;

            return {
              postMessage: function (message, target_url, target) {

                if (!target_url) {
                  return;
                }

                target = target || parent;  // default to parent

                if (window['postMessage']) {
                  // the browser supports window.postMessage, so call it with a targetOrigin
                  // set appropriately, based on the target_url parameter.
                  target['postMessage'](message, target_url.replace(/([^:]+:\/\/[^/]+).*/, '$1'));

                } else if (target_url) {
                  // the browser does not support window.postMessage, so set the location
                  // of the target to target_url#message. A bit ugly, but it works! A cache
                  // bust parameter is added to ensure that repeat messages trigger the callback.
                  target.location = target_url.replace(/#.*$/, '') + '#' + (+new Date) + (cache_bust++) + '&' + message;
                }
              },

              receiveMessage: function (callback, source_origin) {

                // browser supports window.postMessage
                if (window['postMessage']) {
                  // bind the callback to the actual event associated with window.postMessage
                  if (callback) {
                    attached_callback = function (e) {
                      if ((typeof source_origin === 'string' && e.origin !== source_origin)
                          || (Object.prototype.toString.call(source_origin) === "[object Function]" && source_origin(e.origin) === !1)) {
                        return !1;
                      }
                      callback(e);
                    };
                  }
                  if (window['addEventListener']) {
                    window[callback ? 'addEventListener' : 'removeEventListener']('message', attached_callback, !1);
                  } else {
                    window[callback ? 'attachEvent' : 'detachEvent']('onmessage', attached_callback);
                  }
                } else {
                  // a polling loop is started & callback is called whenever the location.hash changes
                  interval_id && clearInterval(interval_id);
                  interval_id = null;

                  if (callback) {
                    interval_id = setInterval(function () {
                      var hash = document.location.hash,
                          re = /^#?\d+&/;
                      if (hash !== last_hash && re.test(hash)) {
                        last_hash = hash;
                        callback({data: hash.replace(re, '')});
                      }
                    }, 100);
                  }
                }
              }
            };
          }();

          /**
           * Generates a random id (with very high probability of uniqueness).
           * @return {string}
           */
          function getRandomId() {
            return (Math.random() + 0.00001).toString(36).substr(2, 5)
                + (Math.random() + 0.0000001).toString(36).substr(2, 5);
          }

          // The code below runs on the parent page, i.e. the page that embeds
          // a FormAssembly form through an <IFRAME> HTML tag.


          // Find FormAssembly iframe(s) and add two URL parameters to their "src" attributes:
          // - hostURL: the URL of this page (the parent page that embeds the form in it)
          // - faIframeUniqueId: a unique ID generated for every IFRAME; it is used
          //   for communication between this page and its IFRAME(s).
          try {
            var iframes = document.querySelectorAll('iframe');
            for (var i = 0; i < iframes.length; i++) {
              if (iframes[i].src.indexOf(/hostURL=/) === -1) {

                // Generate a unique ID and save it in a custom attribute
                var faIframeUniqueId = getRandomId();
                iframes[i].setAttribute('data-fa-iframe-unique-id', faIframeUniqueId);
                // Get SID from URL
                var sid = null;
                try {
                  sid = decodeURIComponent(window.location.search.split('jsid=')[1].split("&")[0]);
                } catch (e) {
                  sid = null;
                }

                // Preserve any other parameter.
                var url = iframes[i].src;
                var hash = "";
                if (url.indexOf('#') !== -1) {
                  hash = "#" + (url.split('#')[1]);
                  url = url.split('#')[0];
                }
                var sep = (url.indexOf("?") === -1) ? "?" : "&";
                iframes[i].src = url + sep + "faIframeUniqueId=" + faIframeUniqueId + "&jsid=" + sid + "&hostURL=" + encodeURIComponent(window.location.href) + hash;


              }
            }
          } catch (e) {
            // iframe not found or unexpected error.
          }


          // Runs on parent page: receive height parameter and update IFRAME's height.
          XD.receiveMessage(function (event) {
            try {

              var messageString = event.data.split(',');
              var heightOrCommand = messageString[0];
              var messageIframeId = messageString[1];
              var source = messageString[2];
              var numIframesApplied = 0;

              // Find the origin of the message. In other words, identify
              // which IFRAME the message has come from, and apply the new height
              // to it.
              var iframes = document.querySelectorAll('iframe');

              for (var i = 0; i < iframes.length; i++) {

                var iframeId = iframes[i].getAttribute('data-fa-iframe-unique-id');
                if (typeof iframeId === "undefined" || iframeId === null || iframeId === "") {
                  iframeId = null;
                }

                if (source == iframes[i].src.replace(/^https?:/, '') || iframeId == messageIframeId) {

                  numIframesApplied++;

                  if (!isNaN(heightOrCommand)) {

                    // save scroll position, to restore after the iframe resize (prevents jumping around)
                    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;


                    // resize iframe
                    iframes[i].height = parseInt(heightOrCommand);

                    // restore scroll position, after a timeout to allow for a repaint.
                    setTimeout( function() {
                      document.documentElement.scrollTop = document.body.scrollTop = scrollTop;
                    },0);

                  }
                  else {
                    if (heightOrCommand == "submitted") {
                      // Will scroll to top of iframe
                      window.scrollBy(0, iframes[i].getBoundingClientRect().top)
                    }
                    if (heightOrCommand == "pagechange") {
                      // Will scroll to top of iframe
                      window.scrollBy(0, iframes[i].getBoundingClientRect().top)
                    }
                  }
                }
              }

              if (!numIframesApplied) {
                // console.log('None of the existing IFRAMES matched the URL, so the message has not been applied.');
              }

            } catch (e) {
              // unexpected message.
            }
          });
        })();
      }
      else {return false;}
    },

    onOEmbedLoad() {
      this.isLoading = false
      this.hasError = false
    },

    onOEmbedError() {
      this.oEmbedFailed = true
      this.isLoading = true
      this.hasError = false
    },

    onIframeLoad() {
      this.isLoading = false
      this.hasError = false
    },

    onIframeError() {
      this.isLoading = false
      this.hasError = true
    }
  }
}
</script>

<style scoped lang="scss">
.slice {
  .slice-embed {
    display: block;
    //border-radius: 0.5rem;
    //overflow: hidden;
    //@include shadow-lg;
    //width: 100%;
    //background: #f8f9fa;

    .iframe-container {
      position: relative;
      width: 100%;
      background: white;
    }

    .loading-indicator {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      color: #666;

      .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }

    .error-fallback {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;

      .error-content {
        text-align: center;
        padding: 2rem;
        max-width: 400px;

        h3 {
          margin: 0 0 1rem 0;
          color: #333;
          font-size: 1.25rem;
        }

        p {
          margin: 0 0 1.5rem 0;
          color: #666;
          line-height: 1.5;
        }

        .external-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #3498db;
          color: white;
          text-decoration: none;
          border-radius: 0.375rem;
          font-weight: 500;
          transition: background-color 0.2s;

          &:hover {
            background: #2980b9;
          }
        }
      }
    }

    // oEmbed specific styles (keep your existing styles)
    /deep/ [data-type="article"] a,
    /deep/ [data-type="website"] a {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      grid-template-areas: "title image" "description image";
      align-items: center;
      align-content: center;
      grid-column-gap: 4rem;
      padding: 2rem;
      text-decoration: none;
      color: inherit;

      h1 {
        grid-area: title;
        font-size: 1.5rem;
        align-self: end;
        margin: 0;
      }

      p {
        align-self: start;
        grid-area: description;
        margin: 0;
      }

      img {
        width: 100%;
        grid-area: image;
        border-radius: 0.25rem;
      }

      .mobile & {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        grid-auto-rows: auto;
        grid-template-areas: "title" "image" "description";
        grid-column-gap: 0;
        grid-row-gap: 1rem;
      }
    }

    /deep/ [data-oembed-type="rich"],
    /deep/ [data-oembed-type="link"] {
      display: grid;
      padding: var(--p5, 2rem);
      align-content: center;
      justify-content: center;

      > blockquote {
        display: none;
      }
    }

    [data-oembed-type="video"] {
      /deep/ iframe {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    .mobile & {
      border-radius: 0;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>